import React from "react"
import { Box, Text, Image, Flex } from "@theme-ui/components"
import { StructuredText } from "react-datocms"
import {
  isHeading,
  isParagraph,
  isList,
  renderRule,
} from "datocms-structured-text-utils"
import ImageGallery from "./blocks/imageGallery"
import { MagicLink } from "../utils/magicLink"
import NumbersGroup from "./blocks/numbersGroup"
import Embed from "./blocks/embed"
import AccordionClosed from "../images/accordion-closed.inline.svg"
import { OutboundLink } from "./link"
import { i18nContext } from "../context/i18nContext"
import ArrowLink from "./arrowLink"
import SlideInComponent from "../components/slideInComponent"

const RichContentStructuredText = ({ text, theme }) => {
  const componentTheme = theme || "light"
  const light = componentTheme === "light" ? "light" : "dark"
  const dark = componentTheme === "light" ? "dark" : "light"
  return (
    <Box
      sx={{
        "p:first-child": {
          marginTop: 0,
        },
        "p:last-child": {
          marginBottom: 0,
        },
      }}
    >
      {text.value && (
        <StructuredText
          data={text}
          renderLinkToRecord={({ record }) => {
            switch (record.__typename) {
              case "DatoCmsInternalLink":
                return <MagicLink item={record} locale={record.locale} />
              default:
                return null
            }
          }}
          renderInlineRecord={({ record }) => {
            switch (record.__typename) {
              case "DatoCmsDocument":
                const document = record
                return (
                  <OutboundLink
                    to={document.documents[0].url}
                    locale={record.locale}
                    sx={{
                      textTransform: "uppercase",
                    }}
                  >
                    <i18nContext.Consumer>
                      {t => (
                        <>
                          {t.download} {record.title}{" "}
                        </>
                      )}
                    </i18nContext.Consumer>
                  </OutboundLink>
                )
              case "DatoCmsInternalLink":
                return <ArrowLink theme={theme} item={record} />
              case "DatoCmsExternalLink":
                return (
                  <Flex
                    sx={{
                      alignItems: "center",
                      textTransform: "uppercase",
                      fontWeight: "400",
                      fontSize: [3, 4, 4, 5],
                      justifyContent: "space-between",
                      pt: [2, 3, 3, 4],
                      mt: [2, 3, 3, 5],
                      borderTop: "2px solid",
                      borderColor: "primary",
                      a: {
                        textDecoration: "none",
                      },
                    }}
                  >
                    <MagicLink
                      target="_blank"
                      href={record.url}
                      sx={{
                        fontSize: [3, 4, 4, 5],
                        display: "flex",
                        width: "100%",
                        justifyContent: "space-between",
                        alignItems: "center",
                        color: theme === "dark" ? "dark" : "primary",
                        svg: {
                          maxWidth: ["28px", "39px"],
                          maxHeight: ["28px", "39px"],
                          fill: theme === "dark" ? "dark" : "primary",
                          transition: "all .2s ease",
                        },
                        "&:hover": {
                          svg: {
                            fill: theme === "dark" ? "dark" : "primary",
                            transform: "rotate(45deg)",
                            transition: "all .2s ease",
                          },
                        },
                      }}
                    >
                      <Box>{record.anchor}</Box>
                      <Box
                        sx={{
                          maxWidth: ["28px", "39px"],
                          maxHeight: ["28px", "39px"],
                          maxWidth: ["50%", "75%", "75%", "100%"],
                        }}
                      >
                        <AccordionClosed />
                      </Box>
                    </MagicLink>
                  </Flex>
                )
              default:
                return null
            }
          }}
          renderBlock={({ record }) => {
            // console.log(record)
            switch (record.__typename) {
              case "DatoCmsImageGallery":
                return (
                  <Box mt={5} mb={5}>
                    <ImageGallery images={record.images} key={record.id} />
                  </Box>
                )
              case "DatoCmsNumbersGroup":
                return (
                  <Box mt={5} mb={5}>
                    <NumbersGroup numbers={record.numbers} key={record.id} />
                  </Box>
                )
              case "DatoCmsEmbed":
                return <Embed code={record.code} fullWidth={record.fullWidth} />
              default:
                return null
            }
          }}
          customRules={[
            renderRule(
              isHeading,
              ({ adapter: { renderNode }, node, children, key }) => {
                return renderNode(
                  () => {
                    return (
                      <SlideInComponent>
                        <Text
                          as={`h${node.level}`}
                          variant={`h${node.level}`}
                          color={"primary"}
                        >
                          {children}
                        </Text>
                      </SlideInComponent>
                    )
                  },
                  { key },
                  children
                )
              }
            ),
            renderRule(
              isParagraph,
              ({ adapter: { renderNode }, node, children, key }) => {
                return renderNode(
                  () => {
                    return (
                      <Text as="p" mb={3} color={dark}>
                        {children}
                      </Text>
                    )
                  },
                  { key },
                  children
                )
              }
            ),
            renderRule(
              isList,
              ({ adapter: { renderNode }, node, children, key }) => {
                return renderNode(
                  () => {
                    return (
                      <Box mt={4}>
                        <Box
                          as={node.style === "numbered" ? "ol" : "ul"}
                          mb={3}
                          sx={{
                            color: dark,
                            listStyle: "none",
                            margin: 0,
                            padding: 0,
                            li: {
                              display: "flex",
                              mb: 1,
                              alignItems: "flex-start",
                              textTransform: "uppercase",
                              position: "relative",
                              "&::before": {
                                display: "flex",
                                marginTop: "12px",
                                WebkitBoxAlign: "center",
                                alignItems: "center",
                                WebkitBoxPack: "center",
                                justifyContent: "center",
                                content: '"—"',
                                marginRight: "16px",
                                marginLeft: "16px",
                                width: "4px",
                                height: "4px",
                                minWidth: "4px",
                                minHeight: "4px",
                                position: "relative",
                                lineHeight: 1.33,
                                color: "dark",
                              },
                              p: {
                                fontSize: [2, 3, 4],
                                mb: 0,
                              },
                            },
                          }}
                        >
                          {children}
                        </Box>
                      </Box>
                    )
                  },
                  { key },
                  children
                )
              }
            ),
          ]}
        />
      )}
    </Box>
  )
}

export default RichContentStructuredText
